import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, resetUserPassword, deleteUser, getRoles } from './adminApi';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role_ids: [],
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [pagination.page, filterActive]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, page_size: pagination.page_size };
      if (search) params.search = search;
      if (filterActive !== null) params.is_active = filterActive;
      
      const result = await getUsers(params);
      setUsers(result.items);
      setPagination(prev => ({ ...prev, total: result.total }));
    } catch (err) {
      console.error('获取用户列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const result = await getRoles();
      setRoles(result);
    } catch (err) {
      console.error('获取角色列表失败:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({ username: '', email: '', password: '', role_ids: [], is_active: true });
    setCurrentUser(null);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit');
    setCurrentUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      role_ids: user.roles?.map(r => r.id) || [],
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const openPasswordModal = (user) => {
    setModalMode('password');
    setCurrentUser(user);
    setFormData({ password: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        await createUser(formData);
      } else if (modalMode === 'edit') {
        await updateUser(currentUser.id, formData);
      } else if (modalMode === 'password') {
        await resetUserPassword(currentUser.id, formData.password);
      }
      closeModal();
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`确定要删除用户 "${user.username}" 吗？`)) return;
    try {
      await deleteUser(user.id);
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.page_size);

  return (
    <div className="users-page">
      <div className="page-header">
        <h2 className="page-title">用户管理</h2>
        <button className="admin-btn admin-btn-primary" onClick={openCreateModal}>
          + 新增用户
        </button>
      </div>

      {/* 搜索和筛选 */}
      <div className="admin-card">
        <form className="admin-search-bar" onSubmit={handleSearch}>
          <input
            type="text"
            className="admin-search-input"
            placeholder="搜索用户名或邮箱..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select 
            className="admin-select" 
            style={{ width: 'auto' }}
            value={filterActive === null ? '' : filterActive.toString()}
            onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
          >
            <option value="">全部状态</option>
            <option value="true">已激活</option>
            <option value="false">已禁用</option>
          </select>
          <button type="submit" className="admin-btn admin-btn-primary">搜索</button>
        </form>
      </div>

      {/* 用户列表 */}
      <div className="admin-card">
        {loading ? (
          <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>用户名</th>
                  <th>邮箱</th>
                  <th>角色</th>
                  <th>状态</th>
                  <th>注册时间</th>
                  <th>最后登录</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      {user.roles?.map(r => (
                        <span key={r.id} className="admin-tag admin-tag-info" style={{ marginRight: 4 }}>
                          {r.name}
                        </span>
                      ))}
                      {(!user.roles || user.roles.length === 0) && '-'}
                    </td>
                    <td>
                      <span className={`admin-tag ${user.is_active ? 'admin-tag-success' : 'admin-tag-error'}`}>
                        {user.is_active ? '正常' : '禁用'}
                      </span>
                    </td>
                    <td>{user.created_at}</td>
                    <td>{user.last_login || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="admin-btn admin-btn-sm admin-btn-default" onClick={() => openEditModal(user)}>
                          编辑
                        </button>
                        <button className="admin-btn admin-btn-sm admin-btn-default" onClick={() => openPasswordModal(user)}>
                          重置密码
                        </button>
                        <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleDelete(user)}>
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="8" className="admin-empty">暂无用户数据</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="admin-pagination">
                <button 
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  上一页
                </button>
                <span>第 {pagination.page} / {totalPages} 页</span>
                <button 
                  disabled={pagination.page === totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 模态框 */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                {modalMode === 'create' ? '新增用户' : modalMode === 'edit' ? '编辑用户' : '重置密码'}
              </h3>
              <button className="admin-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-modal-body">
                {modalMode === 'password' ? (
                  <div className="admin-form-group">
                    <label className="admin-form-label">新密码</label>
                    <input
                      type="password"
                      className="admin-input"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                ) : (
                  <>
                    <div className="admin-form-group">
                      <label className="admin-form-label">用户名</label>
                      <input
                        type="text"
                        className="admin-input"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        required
                        disabled={modalMode === 'edit'}
                      />
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-form-label">邮箱</label>
                      <input
                        type="email"
                        className="admin-input"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    {modalMode === 'create' && (
                      <div className="admin-form-group">
                        <label className="admin-form-label">密码</label>
                        <input
                          type="password"
                          className="admin-input"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          minLength={6}
                        />
                      </div>
                    )}
                    <div className="admin-form-group">
                      <label className="admin-form-label">角色</label>
                      <div className="role-checkboxes">
                        {roles.map(role => (
                          <label key={role.id} className="role-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.role_ids.includes(role.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, role_ids: [...formData.role_ids, role.id] });
                                } else {
                                  setFormData({ ...formData, role_ids: formData.role_ids.filter(id => id !== role.id) });
                                }
                              }}
                            />
                            {role.name}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="admin-form-group">
                      <label className="admin-form-label">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                        账号激活
                      </label>
                    </div>
                  </>
                )}
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn admin-btn-default" onClick={closeModal}>
                  取消
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  {modalMode === 'create' ? '创建' : modalMode === 'edit' ? '保存' : '重置'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;